import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Chip,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { convertVideoToFormats } from '../utils/videoConverter';

export default function VideoAutoConvert({ videoFile, onConvertComplete }) {
  const [converting, setConverting] = useState(false);
  const [steps, setSteps] = useState({});
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const handleConvert = async () => {
    if (!videoFile) return;

    setConverting(true);
    setError(null);
    setDone(false);
    setSteps({});

    try {
      const results = await convertVideoToFormats(videoFile, (info) => {
        setSteps((prev) => ({ ...prev, [info.step]: info }));
      });

      onConvertComplete(results);
      setDone(true);
    } catch (err) {
      console.error('Video conversion error:', err);
      setError(err.message || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  if (!videoFile) {
    return (
      <Box
        sx={{
          p: 3,
          border: '2px dashed #c8dff5',
          borderRadius: 2,
          backgroundColor: '#f8fbff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 120,
          gap: 1,
        }}
      >
        <AutoFixHighIcon sx={{ color: '#b0c8e0', fontSize: 32 }} />
        <Typography variant="body2" sx={{ color: '#aaa', textAlign: 'center' }}>
          Önce Video alanına MP4 yükle,<br />ardından Auto Convert kullanabilirsin.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        border: '2px dashed #3498db',
        borderRadius: 2,
        backgroundColor: '#f0f7ff',
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        sx={{ mb: 1, color: '#2c3e50' }}
      >
        Auto Convert
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
        Yuklenen MP4 videodan otomatik olarak WebM, WebP ve GIF versiyonlarini
        olusturur.
      </Typography>

      {!converting && !done && (
        <Button
          variant="contained"
          startIcon={<AutoFixHighIcon />}
          onClick={handleConvert}
          sx={{
            backgroundColor: '#3498db',
            '&:hover': { backgroundColor: '#2980b9' },
            textTransform: 'none',
            fontWeight: 'bold',
          }}
        >
          Auto Convert
        </Button>
      )}

      {converting && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {['loading', 'webm', 'webp', 'gif'].map((key) => {
            const step = steps[key];
            if (!step) return null;
            return (
              <Box key={key}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {step.done && (
                    <CheckCircleIcon sx={{ color: '#27ae60', fontSize: 18 }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: step.done ? '#27ae60' : '#333',
                      fontWeight: step.done ? 600 : 400,
                    }}
                  >
                    {step.message}
                  </Typography>
                </Box>
                {!step.done && step.progress !== undefined && (
                  <LinearProgress
                    variant="determinate"
                    value={step.progress}
                    sx={{ mt: 0.5, borderRadius: 1, height: 6 }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {done && (
        <Box>
          <Alert severity="success" sx={{ mb: 1.5 }}>
            Tum donusumler tamamlandi! Dosyalar ilgili alanlara eklendi.
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            {['webm', 'webp', 'gif'].map((key) => {
              const step = steps[key];
              return step ? (
                <Chip
                  key={key}
                  label={step.message}
                  color="success"
                  size="small"
                  icon={<CheckCircleIcon />}
                />
              ) : null;
            })}
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AutoFixHighIcon />}
            onClick={() => {
              setDone(false);
              setSteps({});
            }}
            sx={{ textTransform: 'none' }}
          >
            Tekrar Convert Et
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Donusum hatasi: {error}
        </Alert>
      )}
    </Box>
  );
}
